import React, { useState } from 'react';
import { Box, Button, FormLabel, Group, Input, Select, Switch, Textarea, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { JsonData, JsonEditor } from 'json-edit-react';

import { Settings } from 'components/artifact-settings/Settings';
import { useAlert } from 'contexts/alert/useAlert';
import {
  PublicationSettingsQuery,
  QueryConfig,
  useCreateQueryConfigMutation,
  useUpdateQueryConfigMutation
} from 'data/generated/graphql';
import { AllessehQuerySortKeyTypes, AllessehQuerySortOrderType } from 'hooks/useAllessehContentQuery';
import { useAllessehProductAggregations } from 'hooks/useAllessehProductAggregations';
import { useConvertedProperty } from 'hooks/useConvertedProperty';

type Props = {
  existing?: QueryConfig;
  onDone: () => void;
};

const EMPTY_QUERY_BODY = JSON.stringify(
  {
    query: { and: [] },
    sort: [{ key: AllessehQuerySortKeyTypes.LiveDate, order: AllessehQuerySortOrderType.DESC }]
  },
  null,
  2
);

const BaseQueryConfigForm = ({ existing, onDone }: Props) => {
  const publicationKey = useConvertedProperty() ?? '';
  const { alertError, alertSuccess } = useAlert();
  const queryClient = useQueryClient();

  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [category, setCategory] = useState<'type' | 'brand'>(existing?.category === 'brand' ? 'brand' : 'type');
  const [contentTypeLabel, setContentTypeLabel] = useState<string>(
    (existing?.contentTypeLabel as string | undefined) ?? ''
  );
  const [isContentTypeLabelDirty, setIsContentTypeLabelDirty] = useState(false);
  const [iconSvgPath, setIconSvgPath] = useState<string>((existing?.iconSvgPath as string | undefined) ?? '');
  const [queryBodyStr, setQueryBodyStr] = useState(
    existing?.queryBody ? JSON.stringify(existing.queryBody, null, 2) : EMPTY_QUERY_BODY
  );
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [bodyError, setBodyError] = useState('');

  const patchBaseQueryConfigs = (
    patch: (configs: QueryConfig[]) => QueryConfig[]
  ): ((old: PublicationSettingsQuery | undefined) => PublicationSettingsQuery | undefined) =>
    (old) => {
      if (!old?.publicationSetting) return old;
      return {
        ...old,
        publicationSetting: {
          ...old.publicationSetting,
          baseQueryConfigs: patch((old.publicationSetting.baseQueryConfigs ?? []) as QueryConfig[])
        }
      };
    };

  const { mutate: createMutation, isLoading: isCreating } = useCreateQueryConfigMutation({
    onSuccess: (response) => {
      const created = response.createQueryConfig as QueryConfig;
      queryClient.setQueriesData<PublicationSettingsQuery>(
        { queryKey: ['PublicationSettings'] },
        patchBaseQueryConfigs((configs) => [...configs, created])
      );
    }
  });
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateQueryConfigMutation({
    onSuccess: (response) => {
      const updated = response.updateQueryConfig as QueryConfig;
      queryClient.setQueriesData<PublicationSettingsQuery>(
        { queryKey: ['PublicationSettings'] },
        patchBaseQueryConfigs((configs) => configs.map((qc) => (qc.id === updated.id ? updated : qc)))
      );
    }
  });

  const isLoading = isCreating || isUpdating;

  const { items: productItems, isLoading: isProductsLoading } = useAllessehProductAggregations();

  const validateBody = (val: string): Record<string, unknown> | null => {
    try {
      return JSON.parse(val) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (!isContentTypeLabelDirty) {
      setContentTypeLabel(newName);
    }
    if (newName) {
      const base = validateBody(queryBodyStr) ?? {
        query: { and: [] },
        sort: [{ key: AllessehQuerySortKeyTypes.LiveDate, order: AllessehQuerySortOrderType.DESC }]
      };
      const baseQuery =
        base.query !== null && typeof base.query === 'object' && !Array.isArray(base.query)
          ? (base.query as Record<string, unknown>)
          : {};
      const existingAnd = Array.isArray(baseQuery.and) ? (baseQuery.and as unknown[]) : [];
      const andClauses = (existingAnd as Record<string, unknown>[]).filter(
        (c) => (c.terms as Record<string, unknown> | undefined)?.key !== 'Product'
      );
      andClauses.push({ terms: { key: 'Product', value: [newName] } });
      setQueryBodyStr(JSON.stringify({ ...base, query: { ...baseQuery, and: andClauses } }, null, 2));
    }
  };

  const handleBodyChange = (val: string) => {
    setQueryBodyStr(val);
    setBodyError(validateBody(val) ? '' : 'Invalid JSON');
  };

  // Accepts a raw d="..." string, a single <path>, or full SVG markup with multiple <path> elements.
  // Extracts all d values and joins them into one compound path string.
  const normalizeSvgPath = (raw: string): string | undefined => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const matches = [...trimmed.matchAll(/\bd="([^"]+)"/g)].map((m) => m[1]);
    return matches.length > 0 ? matches.join(' ') : trimmed;
  };

  // Note: the backend create/update/delete mutations use read-modify-write on the full baseQueryConfigs array.
  // Concurrent edits for the same publication (e.g. two admins saving simultaneously) can overwrite each other.
  // Accepted risk: this is an admin-only path that is never called concurrently in practice.
  const handleSubmit = () => {
    const queryBody = validateBody(queryBodyStr);
    if (!queryBody) {
      setBodyError('Invalid JSON — fix before saving');
      return;
    }
    if (!name.trim()) return;

    const trimmedLabel = contentTypeLabel.trim() || undefined;
    const trimmedSvgPath = normalizeSvgPath(iconSvgPath);

    const callbacks = {
      onSuccess: () => {
        alertSuccess(`Query "${name}" ${existing ? 'updated' : 'created'}`);
        onDone();
      },
      onError: (err: unknown) => {
        alertError(err instanceof Error ? err.message : 'Failed to save query');
      }
    };

    if (existing) {
      updateMutation(
        {
          input: {
            publicationKey,
            id: existing.id,
            name,
            description,
            category,
            contentTypeLabel: trimmedLabel ?? null,
            iconSvgPath: trimmedSvgPath ?? null,
            queryBody,
            enabled
          }
        },
        callbacks
      );
    } else {
      createMutation(
        {
          input: {
            publicationKey,
            name,
            description,
            category,
            contentTypeLabel: trimmedLabel,
            iconSvgPath: trimmedSvgPath,
            queryBody,
            enabled
          }
        },
        callbacks
      );
    }
  };

  return (
    <Box padding={{ all: 'md' }}>
      <Settings title={existing ? 'Edit Query' : 'New Query'}>
        <Group gap="md" direction="column">
          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Allesseh Product" labelPosition="top" fullWidth required />
              <Select value={name} onChange={handleNameChange} disabled={isProductsLoading}>
                <option value="">— select a product —</option>
                {productItems.map((item) => (
                  <option key={item.id} value={String(item.value)}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Content Type Label" labelPosition="top" fullWidth />
              <Input
                id="qc-content-type-label"
                value={contentTypeLabel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setContentTypeLabel(e.target.value);
                  setIsContentTypeLabelDirty(true);
                }}
                placeholder="Display name in Type filter (defaults to Name if blank)"
              />
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Category" labelPosition="top" fullWidth required />
              <Select
                value={category}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value as 'type' | 'brand')}
              >
                <option value="type">Type</option>
                <option value="brand">Brand</option>
              </Select>
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Query Body (JSON)" labelPosition="top" fullWidth required />
              <div style={{ isolation: 'isolate' }}>
                <JsonEditor
                  data={validateBody(queryBodyStr) ?? {}}
                  setData={(val: JsonData) => handleBodyChange(JSON.stringify(val, null, 2))}
                  showIconTooltips
                  showCollectionCount="when-closed"
                  indent={4}
                  maxWidth="100%"
                  restrictDrag
                />
              </div>
              {bodyError && (
                <Typography variant="note" color="lava">
                  {bodyError}
                </Typography>
              )}
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Icon SVG Path" labelPosition="top" fullWidth />
              <Textarea
                id="qc-icon-svg-path"
                value={iconSvgPath}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setIconSvgPath(e.target.value)}
                placeholder='Optional — paste a d="..." string, a <path> element, or full SVG markup (multi-path supported)'
                rows={1}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              <Typography variant="note" color="asphalt">
                Icon paths must use a <strong>0 0 48 48</strong> viewBox coordinate space. Paths from other coordinate
                systems (e.g. 24×24, 512×512) will appear incorrectly sized.
              </Typography>
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="xs" direction="column" fullWidth>
              <FormLabel label="Description" labelPosition="top" fullWidth />
              <Input
                id="qc-description"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                placeholder="Optional — what does this query retrieve?"
              />
            </Group>
          </Group.Item>

          <Group.Item fullWidth>
            <FormLabel label="Enabled" labelPosition="right" fullWidth>
              <Switch id="qc-enabled" checked={enabled} onChange={() => setEnabled((v) => !v)} />
            </FormLabel>
          </Group.Item>

          <Group.Item fullWidth>
            <Group gap="sm">
              <Button onClick={onDone} disabled={isLoading} secondary>
                Cancel
              </Button>
              <Button primary onClick={handleSubmit} disabled={isLoading || !!bodyError || !name.trim()}>
                {isLoading ? 'Saving…' : 'Save'}
              </Button>
            </Group>
          </Group.Item>
        </Group>
      </Settings>
    </Box>
  );
};

export default BaseQueryConfigForm;
